import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    width: "100%",
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const Webdav = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);

  const [baseUrl, setBaseUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [defaultFolder, setDefaultFolder] = useState("SEM_PASTA");

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/webdav/config");
      setBaseUrl(data.baseUrl || "");
      setUsername(data.username || "");
      setDefaultFolder(data.defaultFolder || "SEM_PASTA");
      setHasPassword(!!data.hasPassword);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/webdav/config", {
        baseUrl,
        username,
        password: password || undefined,
        defaultFolder,
      });
      toast.success("Configuração do WebDAV salva com sucesso!");
      setPassword("");
      fetchConfig();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/webdav/test-connection", {
        baseUrl,
        username,
        password: password || undefined,
      });
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, message: "Falha ao testar conexão." });
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Integração com o servidor WebDAV usado pra guardar os arquivos trocados
          com os contatos (ex: Nextcloud/ownCloud). Os arquivos recebidos de cada
          contato são organizados em <strong>pasta do contato / ano</strong> dentro
          da pasta raiz configurada aqui.
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.field}>
        <TextField
          label="URL base (com a pasta raiz)"
          placeholder="https://sistemacontec.ddns.net:4004/remote.php/dav/files/josney/CLIENTESPF"
          value={baseUrl}
          onChange={e => setBaseUrl(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={6} className={classes.field}>
        <TextField
          label="Usuário"
          value={username}
          onChange={e => setUsername(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={6} className={classes.field}>
        <TextField
          label="Senha"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder={hasPassword ? "•••••••• (deixe em branco pra manter)" : ""}
          fullWidth
          variant="outlined"
          size="small"
        />
      </Grid>
      <Grid item xs={12} className={classes.field}>
        <TextField
          label="Pasta padrão (quando o contato não tem pasta configurada)"
          value={defaultFolder}
          onChange={e => setDefaultFolder(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />
      </Grid>
      {testResult && (
        <Grid item xs={12} className={classes.field}>
          <Alert severity={testResult.success ? "success" : "error"}>
            {testResult.message}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} className={classes.actions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleTest}
          disabled={testing || !baseUrl || !username}
        >
          {testing ? <CircularProgress size={20} /> : "Testar conexão"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !baseUrl || !username}
        >
          {saving ? <CircularProgress size={20} /> : "Salvar"}
        </Button>
      </Grid>
    </Grid>
  );
};

export default Webdav;
